import type { LucideIcon } from 'lucide-react-native';
import { ChevronRightIcon, XIcon } from 'lucide-react-native';
import { Pressable, View } from 'react-native';

import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';

type EventEditorRowProps = Readonly<{
  accessibilityHint?: string;
  destructive?: boolean;
  disabled?: boolean;
  icon: LucideIcon;
  label: string;
  onClear?: () => void;
  onPress?: () => void;
  value?: string;
}>;

export function EventEditorRow({
  accessibilityHint,
  destructive = false,
  disabled = false,
  icon,
  label,
  onClear,
  onPress,
  value,
}: EventEditorRowProps) {
  return (
    <View className="min-h-16 flex-row items-center border-b border-border">
      <Pressable
        accessibilityHint={accessibilityHint}
        accessibilityLabel={value ? `${label}, ${value}` : label}
        accessibilityRole="button"
        accessibilityState={{ disabled }}
        disabled={disabled}
        onPress={onPress}
        className="min-w-0 flex-1 flex-row items-center gap-4 px-1 py-3 active:opacity-60"
      >
        <Icon
          as={icon}
          className={destructive ? 'size-5 text-destructive' : 'size-5 text-foreground'}
        />
        <View className="min-w-0 flex-1 gap-0.5">
          <Text className={destructive ? 'text-destructive' : undefined}>{label}</Text>
          {value ? (
            <Text variant="muted" numberOfLines={2}>
              {value}
            </Text>
          ) : null}
        </View>
        {onClear ? null : <Icon as={ChevronRightIcon} className="size-5 text-muted-foreground" />}
      </Pressable>
      {onClear ? (
        <Pressable
          accessibilityLabel={`Clear ${label.toLowerCase()}`}
          accessibilityRole="button"
          hitSlop={10}
          onPress={(event) => {
            event.stopPropagation();
            onClear();
          }}
          className="size-11 items-center justify-center rounded-full active:bg-muted"
        >
          <Icon as={XIcon} className="size-5 text-muted-foreground" />
        </Pressable>
      ) : null}
    </View>
  );
}
